/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type MemoExoticComponent,
} from 'react';

import { jsx } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import InlineEdit from '@atlaskit/inline-edit';
import { Manager, Popper, Reference } from '@atlaskit/popper';
import Portal from '@atlaskit/portal';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Select, { components, type ValueType } from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import type { FieldBaseProps } from '../common/types';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import type { JiraCascadingSelectField_Platform$key } from './__generated__/JiraCascadingSelectField_Platform.graphql';
import { ValidationPopup } from './ValidationPopup';

const selectLabel = defineMessage({
	id: 'jira.jira.jira-platform-fields.cascading-select-field.label',
	defaultMessage: 'Select {fieldName}',
	description: 'Label for the Jira cascading select dropdown',
});

const noOptionMessage = defineMessage({
	id: 'jira.jira.jira-platform-fields.cascading-select-field.no-options',
	defaultMessage: 'None',
	description:
		'Message displayed when there is no option selected in the Jira cascading select dropdown',
});

const noParentOptionMessage = defineMessage({
	id: 'jira.jira.jira-platform-fields.cascading-select-field.no-parent-option',
	defaultMessage: 'Parent option is required when child option is selected',
	description:
		'Message displayed when there is a child option selected but no parent option in the Jira cascading select dropdown',
});

type CascadingSelectValue = {
	child: CascadingSelectChildValue;
	id: string | null;
} | null;

type CascadingSelectChildValue = {
	id: string;
} | null;

export type Props = FieldBaseProps & {
	field: JiraCascadingSelectField_Platform$key;
	onChange?: (newValue: CascadingSelectValue) => void;
};

type SelectOption = {
	id: string;
	value: string;
};

// Type guards for runtime type checking
const isCascadingSelectValue = (value: unknown): value is CascadingSelectValue => {
	if (value === null) {
		return true;
	}
	return (
		typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
	);
};

const isCascadingSelectChildValue = (value: unknown): value is CascadingSelectChildValue => {
	if (value === null) {
		return true;
	}
	return (
		typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
	);
};

const styles = cssMap({
	readViewContainer: {
		height: '40px',
		alignItems: 'center',
		display: 'flex',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		width: '100%',
		boxSizing: 'border-box',
	},
	popperContainer: {
		zIndex: 510,
		// The InlineEdit component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	editableReadViewContainer: {
		width: '100%',
		// Cursor pointer to indicate that the field is editable
		cursor: 'pointer',
	},
	editViewContainer: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.075'),
		overflow: 'visible',
	},
	control: {
		width: '100%',
		boxSizing: 'border-box',
	},
	container: {
		display: 'flex',
		flex: '1 1 0',
		minWidth: '0px',
		width: '100%',
	},
	valueContainer: {
		minWidth: '0px',
	},
	childSelector: {
		width: '100%',
	},
});

/**
 * `JiraCascadingSelectField` is a memoized React component that renders a Jira cascading select field,
 * supporting read-only mode (as of current implementation). It displays the current option and allows
 * users to select from available options if the field is editable. The component uses
 * Relay fragments to fetch option data and renders options with icons and labels.
 *
 * @param field - The Relay fragment reference for the Jira option field.
 * @param fieldValue - The currently suggested option value.
 * @param onChange - Callback invoked when the option selection changes.
 * @returns A JSX element representing the option field, or `null` if no option is found.
 */

export const JiraCascadingSelectField: MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: Props) => JSX.Element
> = memo(({ fieldValue, field, isFieldEditable = true, onChange }: Props): JSX.Element => {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<JiraCascadingSelectField_Platform$key>(
		graphql`
			fragment JiraCascadingSelectField_Platform on JiraCascadingSelectField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				cascadingOptions {
					edges {
						node {
							parentOptionValue {
								optionId
								value
							}
							childOptionValues {
								optionId
								value
							}
						}
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
		field,
	);

	const { formatMessage } = useIntl();

	const { isEditable, isRequired } = data.fieldConfig ?? {};
	const fieldName = data.name;
	const FIELD_TYPE_FOR_ANALYTICS = 'CascadingSelectField';
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const validateRequired = useValidateRequiredField({
		isRequired: !!isRequired,
		isEditable: !!isEditable,
		fieldName,
		fieldTypeForAnalytics: FIELD_TYPE_FOR_ANALYTICS,
	});

	// Refs and field configuration
	const parentRef = useRef<AtlaskitSelectRefType | null>(null);
	const childRef = useRef<AtlaskitSelectRefType | null>(null);

	// Menu states for controlling display of parent and child dropdowns
	const [isEditing, setIsEditing] = useState(false);

	// Get parent options from graphql fragment
	const parentOptions: SelectOption[] = useMemo(() => {
		return (
			data.cascadingOptions?.edges?.filter(Boolean).map((edge) => ({
				id: String(edge?.node?.parentOptionValue?.optionId ?? ''),
				value: String(edge?.node?.parentOptionValue?.value ?? ''),
			})) ?? []
		);
	}, [data.cascadingOptions?.edges]);

	// Use currently selected parent option or the first parent option if no option is selected
	const initialParentOption = useMemo(() => {
		if (isCascadingSelectValue(fieldValue) && fieldValue !== null) {
			const fieldParentOption = parentOptions.find((option) => option.id === fieldValue.id) || null;
			if (fieldParentOption) {
				return fieldParentOption;
			}
		}
		// Return null if field is not required
		if (!isRequired) {
			return null;
		}
		// If no parent is fetched for a required field, set the first parent option as the selected option
		const firstParentOption = parentOptions[0];
		onChange?.({
			id: firstParentOption?.id ?? null,
			child: null,
		});
		return firstParentOption;
	}, [onChange, fieldValue, isRequired, parentOptions]);

	// States for controlling parent dropdown and selections
	const [selectedParentOption, setSelectedParentOption] = useState<SelectOption | null>(
		initialParentOption,
	);

	// Get child options from graphql fragment based on selected parent option
	const childOptions: SelectOption[] = useMemo(() => {
		if (!selectedParentOption?.value) {
			return [];
		}

		const parentEdge = data?.cascadingOptions?.edges?.find(
			(edge) => edge?.node?.parentOptionValue?.value === selectedParentOption?.value,
		);

		return (
			parentEdge?.node?.childOptionValues?.map((childOption) => ({
				id: String(childOption?.optionId ?? ''),
				value: String(childOption?.value ?? ''),
			})) ?? []
		);
	}, [selectedParentOption, data?.cascadingOptions?.edges]);

	// Use currently selected child option or the first child option if no option is selected
	const initialChildOption = useMemo(() => {
		if (isCascadingSelectValue(fieldValue) && fieldValue !== null) {
			const child = fieldValue.child;
			if (isCascadingSelectChildValue(child) && child !== null) {
				const fieldChildOption = childOptions.find((option) => option.id === child.id) || null;
				if (fieldChildOption) {
					return fieldChildOption;
				}
			}
		}
		if (!isRequired || !childOptions.length) {
			return null;
		}
		// If no child is fetched for a required field, set the first child option as the selected option
		const firstChildOption = childOptions[0];
		onChange?.({
			id: selectedParentOption?.id ?? null,
			child: firstChildOption?.id ? { id: firstChildOption.id } : null,
		});
		return firstChildOption;
	}, [onChange, fieldValue, isRequired, childOptions, selectedParentOption?.id]);

	const [selectedChildOption, setSelectedChildOption] = useState<SelectOption | null>(
		initialChildOption,
	);

	// Hide child selector when no child options are available
	const showChildSelector = useMemo(
		() => selectedParentOption?.value && (selectedChildOption?.value || childOptions.length > 0),
		[childOptions.length, selectedChildOption?.value, selectedParentOption?.value],
	);

	const originalParentOption = useRef<SelectOption | null>(null);
	const originalChildOption = useRef<SelectOption | null>(null);

	const currentValue = useMemo<string>(() => {
		if (!selectedParentOption?.value) {
			return '';
		}
		if (selectedChildOption?.value) {
			return `${selectedParentOption.value} - ${selectedChildOption.value}`;
		}
		return selectedParentOption.value;
	}, [selectedParentOption?.value, selectedChildOption?.value]);

	const selectComponentProps = {
		appearance: 'default',
		getOptionValue: (option: SelectOption) => option.value,
		getOptionLabel: (option: SelectOption) => option.value,
		tabSelectsValue: true,
		blurInputOnSelect: false,
		closeMenuOnSelect: true,
		isSearchable: true,
		isRequired: !!isRequired,
		placeholder: formatMessage(noOptionMessage),
		isMulti: false,
		menuPortalTarget: getDocument()?.body,
		menuPlacement: 'auto',
		menuPosition: 'fixed',
		styles: {
			menuPortal: (base: any) => ({ ...base, zIndex: layers.modal() }),
		},
	} as const;

	// Track which menu should be focused/open
	const [currentMenuOpen, setCurrentMenuOpen] = useState<'parent' | 'child' | null>(null);
	const currentMenuIsOpenRef = useRef<'parent' | 'child' | null>(null);
	// Keep ref in sync with state for synchronous updates
	const updateMenuOpen = (value: 'parent' | 'child' | null) => {
		setCurrentMenuOpen(value);
		currentMenuIsOpenRef.current = value;
	};

	// Focus the appropriate dropdown when the menu state changes
	// (focus does not always align with user actions without autoFocus prop, but it does not work with Portal/Popper implementation)
	useEffect(() => {
		// Use setTimeout to ensure the Select components have been mounted
		const timeoutId = setTimeout(() => {
			if (currentMenuIsOpenRef.current === 'parent') {
				parentRef.current?.focus?.();
			} else if (currentMenuIsOpenRef.current === 'child') {
				childRef.current?.focus?.();
			}
		}, 0);
		return () => clearTimeout(timeoutId);
	}, [currentMenuOpen, isEditing]);

	const onParentChange = (newValue: ValueType<SelectOption | null>) => {
		// Return focus to parent and reset selections upon parent clear
		if (newValue === null) {
			setSelectedParentOption(null);
			setSelectedChildOption(null);
			setTimeout(() => {
				parentRef.current?.focus();
			}, 0);
			updateMenuOpen('parent');
			return;
		}

		setSelectedParentOption(newValue);
		setSelectedChildOption(null);

		// Compute child options for the new parent for immediate use
		const newParentEdge = data?.cascadingOptions?.edges?.find(
			(edge) => edge?.node?.parentOptionValue?.optionId === newValue.id,
		);
		const newChildOptions = newParentEdge?.node?.childOptionValues ?? [];
		// Only open child menu if there are child options
		if (newChildOptions.length > 0) {
			updateMenuOpen('child');
		} else {
			updateMenuOpen(null);
			setTimeout(() => {
				parentRef.current?.focus();
			}, 0);
		}
	};

	const onChildChange = (newValue: ValueType<SelectOption | null>) => {
		setSelectedChildOption(newValue);
		// Maintain focus on child select after selection (as per list view implementation behaviour)
		setTimeout(() => {
			childRef.current?.focus();
		}, 0);
	};

	const onParentMenuClose = () => {
		// Keep parent menu open if no selection is made and the field is required
		if (isRequired && !selectedParentOption?.id) {
			parentRef.current?.focus();
		}
	};

	const validate = useCallback(
		(_value: string) => {
			// Check if the field is required and value is empty
			const validateError = validateRequired(currentValue);
			if (validateError) {
				updateMenuOpen('parent');
				return validateError;
			}

			// Guard against unlikely case where child option is selected but parent option is not
			if (!selectedParentOption?.id && selectedChildOption?.id) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'validationError',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName: fieldName,
						fieldType: FIELD_TYPE_FOR_ANALYTICS,
						isRequired,
						isEditable,
					},
				);
				return formatMessage(noParentOptionMessage);
			}
		},
		[
			createAnalyticsEvent,
			fieldName,
			isEditable,
			validateRequired,
			formatMessage,
			selectedParentOption,
			selectedChildOption,
			currentValue,
			isRequired,
		],
	);

	const onEdit = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		// Save original selections for cancel
		originalParentOption.current = selectedParentOption;
		originalChildOption.current = selectedChildOption;
		// Enter edit mode and open parent menu automatically
		updateMenuOpen('parent');
		setIsEditing(true);
	}, [
		createAnalyticsEvent,
		fieldName,
		isEditable,
		isRequired,
		selectedParentOption,
		selectedChildOption,
	]);

	const onCancel = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		// Restore original selections
		setSelectedParentOption(originalParentOption.current ?? null);
		setSelectedChildOption(originalChildOption.current ?? null);
		setIsEditing(false);
	}, [createAnalyticsEvent, fieldName, isRequired, isEditable]);

	const onConfirm = useCallback(() => {
		// Save the new value
		onChange?.({
			id: selectedParentOption?.id ?? null,
			child: selectedChildOption?.id ? { id: selectedChildOption.id } : null,
		});
		setIsEditing(false);
	}, [onChange, selectedParentOption, selectedChildOption]);

	// Update menu open state for parent in the case where user immediately returns to the parent select
	// without any actions after parent select (currentMenuOpen does not get updated otherwise)
	const onParentFocus = () => {
		// Condition required to not keep menu open when no child options are available
		if (childOptions.length > 0) {
			updateMenuOpen('parent');
		}
	};

	const ReadView = memo(({ value }: { value: string }) => {
		return (
			<Box xcss={styles.readViewContainer}>
				<Text maxLines={1}>{value || formatMessage(noOptionMessage)}</Text>
			</Box>
		);
	});

	const EditView = memo(() => {
		return (
			<Box xcss={styles.editViewContainer} backgroundColor="color.background.input">
				<Select<SelectOption>
					{...selectComponentProps}
					ref={parentRef}
					label={formatMessage(selectLabel, { fieldName: data.name })}
					options={parentOptions}
					value={parentOptions.find((option) => option.value === selectedParentOption?.value)}
					formatOptionLabel={(option) => option.value}
					onFocus={onParentFocus}
					menuIsOpen={currentMenuOpen === 'parent'}
					onMenuOpen={() => updateMenuOpen('parent')}
					onMenuClose={onParentMenuClose}
					onChange={onParentChange}
					isClearable
					components={{
						Control: (props) => <components.Control {...props} xcss={styles.control} />,
						SelectContainer: (props) => (
							<components.SelectContainer {...props} xcss={styles.container} />
						),
						ValueContainer: (props) => (
							<components.ValueContainer {...props} xcss={styles.valueContainer} />
						),
						DropdownIndicator: (props) => <components.DropdownIndicator {...props} />,
					}}
				/>
				{showChildSelector && (
					<Box xcss={styles.childSelector} backgroundColor="color.background.input">
						<Select<SelectOption>
							ref={childRef}
							label={formatMessage(selectLabel, { fieldName: data.name })}
							options={childOptions}
							value={childOptions.find((option) => option.value === selectedChildOption?.value)}
							menuIsOpen={currentMenuOpen === 'child'}
							onMenuOpen={() => updateMenuOpen('child')}
							onMenuClose={() => updateMenuOpen(null)}
							onChange={onChildChange}
							isClearable={selectedChildOption !== null}
							components={{
								Control: (props) => <components.Control {...props} xcss={styles.control} />,
								SelectContainer: (props) => (
									<components.SelectContainer {...props} xcss={styles.container} />
								),
								ValueContainer: (props) => (
									<components.ValueContainer {...props} xcss={styles.valueContainer} />
								),
								DropdownIndicator: (props) => <components.DropdownIndicator {...props} />,
							}}
							{...selectComponentProps}
						/>
					</Box>
				)}
			</Box>
		);
	});

	const ReadViewComponent = useCallback(() => {
		return <ReadView value={currentValue} />;
	}, [currentValue, ReadView]);

	const isEditableProp = isEditable && isFieldEditable;

	// Render only the read view component if the field is not editable
	if (!isEditableProp) {
		return <ReadView value={currentValue} />;
	}

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					// Causes InlineEdit read view to be redundant, but is required for the Portal reference
					<Box
						role="none"
						ref={ref}
						xcss={styles.editableReadViewContainer}
						onClick={!isEditing ? onEdit : undefined}
					>
						{!isEditing && <ReadView value={currentValue} />}
					</Box>
				)}
			</Reference>

			{isEditing && (
				<Portal zIndex={layers.modal()}>
					<Popper
						placement="bottom-start"
						strategy="fixed"
						modifiers={[
							{
								name: 'offset',
								options: {
									offset: ({ reference }) => [0, -reference.height],
								},
							},
							{
								name: 'sameWidth',
								enabled: true,
								phase: 'beforeWrite',
								requires: ['computeStyles'],
								fn: ({ state }) => {
									state.styles.popper.width = `${state.rects.reference.width}px`;
								},
								effect: ({ state }) => {
									state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
								},
							},
						]}
					>
						{({ ref: popperRef, style }) => (
							<Box
								ref={popperRef}
								xcss={styles.popperContainer}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Popper requires dynamic positioning styles
								style={style}
							>
								<Box xcss={styles.editViewContainer}>
									<InlineEdit
										isEditing={true}
										defaultValue={currentValue}
										editButtonLabel={currentValue || formatMessage(noOptionMessage)}
										editView={({ errorMessage, ...fieldProps }) => (
											<ValidationPopup
												errorMessage={errorMessage}
												trigger={(triggerProps) => (
													<Box ref={triggerProps.ref}>
														<EditView />
													</Box>
												)}
												placement="right"
												showInvalidPopup={fieldProps.isInvalid}
											/>
										)}
										readView={ReadViewComponent}
										onConfirm={onConfirm}
										onCancel={onCancel}
										onEdit={onEdit}
										readViewFitContainerWidth
										validate={validate}
									/>
								</Box>
							</Box>
						)}
					</Popper>
				</Portal>
			)}
		</Manager>
	);
});
